import "./CountertopUniversityForm.scss"
import { useEffect, useState } from "react";
import { useGetCountertopUniversityQuiz } from "../api/get-countertop-university-quiz";
import { Button, Popup } from "devextreme-react";
import { useSaveCountertopUniversityQuizAnswer } from "../api/save-countertop-university-quiz";
import { showErrorMessage } from "../../../../utils/messages";
import IncompleteVideoIcon from '../../../../assets/images/Incomplete Question Item Icon.svg';
import CompletedItemIcon from '../../../../assets/images/Completed Item Icon.svg';
import { useQueryClient } from "@tanstack/react-query";
import { CountertopUniversityDetail } from "../types/CountertopUniversityDetail";
import { useCountertopUniversity } from "../contexts/countertopUniversityContext";
import CountertopUniversityQuestionAttempts from "./CountertopUniversityQuestionAttempts";
import CountertopProfileQuizBlock from "./CountertopProfileQuizBlock";

export default function CountertopUniversityQuiz(props: { item: CountertopUniversityDetail, countertopUniversityID: number, onQuizComplete?: () => void }) {

    const queryClient = useQueryClient();
    const context = useCountertopUniversity();
    const recordsData = useGetCountertopUniversityQuiz(props.countertopUniversityID, context.profileID);

    const [isQuestionPopupVisible, setIsQuestionPopupVisible] = useState(false);
    const [shouldSilentlySave, setShouldSilentlySave] = useState(false);

    const [isAnswerAChecked, setIsAnswerAChecked] = useState(false);
    const [isAnswerBChecked, setIsAnswerBChecked] = useState(false);
    const [isAnswerCChecked, setIsAnswerCChecked] = useState(false);
    const [isAnswerDChecked, setIsAnswerDChecked] = useState(false);

    const [isAnswerADisabled, setIsAnswerADisabled] = useState(false);
    const [isAnswerBDisabled, setIsAnswerBDisabled] = useState(false);
    const [isAnswerCDisabled, setIsAnswerCDisabled] = useState(false);
    const [isAnswerDDisabled, setIsAnswerDDisabled] = useState(false);

    const [questionStatusA, setQuestionStatusA] = useState("");
    const [questionStatusB, setQuestionStatusB] = useState("");
    const [questionStatusC, setQuestionStatusC] = useState("");
    const [questionStatusD, setQuestionStatusD] = useState("");

    const [finalAnswer, setFinalAnswer] = useState("");
    const [textQuestionSummary, setTextQuestionSummary] = useState("");
    const [textQuestionSummaryCSS, setTextQuestionSummaryCSS] = useState("");
    const [currentQuestionNumber, setCurrentQuestionNumber] = useState(0);

    const [isButtonSubmitVisible, setIsButtonSubmitVisible] = useState(true);
    const [isButtonRetryVisible, setIsButtonRetryVisible] = useState(false);
    const [isButtonDoneVisible, setIsButtonDoneVisible] = useState(false);

    const [isCorrect, setIsCorrect] = useState(false);
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

    useEffect(() => {
        recordsData.refetch();

        // Expand the last container if there are any containers present
        setTimeout(() => {
            if (recordsData.data?.length > 0) {
                setExpandedIndex(recordsData.data.length - 1); // expand last item
            }
        }, 450);
    }, [context.profileID, finalAnswer]);

    useEffect(() => {
        setCurrentQuestionNumber(0);
        setIsQuestionPopupVisible(false);
    }, [props.countertopUniversityID]);

    const saveAnswerQuiz = useSaveCountertopUniversityQuizAnswer(
        recordsData.data.length > 0 ? recordsData.data[currentQuestionNumber].countertopUniversityQuizID : 0,
        finalAnswer === "" || finalAnswer === null || finalAnswer === undefined ? "a" : finalAnswer,
        context.profileID || null,
        isCorrect,
        async () => {
            if (isCorrect 
                && currentQuestionNumber + 1 < recordsData.data.length 
                && !recordsData.data[currentQuestionNumber + 1].answerDetails.some(x => x.isCorrect)
            ) {
                setCurrentQuestionNumber(currentQuestionNumber + 1);
                resetQuestionPopup(currentQuestionNumber + 1);
            }
        },
        (error) => showErrorMessage(error)
    );

    const toggleQuestionPopup = () => {
        setIsQuestionPopupVisible(!isQuestionPopupVisible);
    };

    const checkAnswer = () => {
        setIsAnswerADisabled(true);
        setIsAnswerBDisabled(true);
        setIsAnswerCDisabled(true);
        setIsAnswerDDisabled(true);

        let answer = "";
        if (isAnswerAChecked)
            answer += answer.length > 0 ? ", a" : "a";

        if (isAnswerBChecked)
            answer += answer.length > 0 ? ", b" : "b";

        if (isAnswerCChecked)
            answer += answer.length > 0 ? ", c" : "c";

        if (isAnswerDChecked)
            answer += answer.length > 0 ? ", d" : "d";

        setFinalAnswer(answer);

        let hasIncorrect = controlResponses(answer, recordsData.data[currentQuestionNumber].correctAnswer, true).hasIncorrect;
        setTextQuestionSummary(hasIncorrect ? 'Answer is incorrect. Please try again.' : 'Your answer is correct!');
        setTextQuestionSummaryCSS(hasIncorrect ? 'countertop-question-summary-incorrect' : 'countertop-question-summary-correct');

        if (hasIncorrect) {
            setIsButtonSubmitVisible(false);
            setIsButtonRetryVisible(true);
            setIsButtonDoneVisible(false);
        } else {
            setIsButtonSubmitVisible(false);
            setIsButtonRetryVisible(false);
            setIsButtonDoneVisible(true);
        }

        return !hasIncorrect;
    };

    const controlResponses = (userResponse: string, correctAnswer: string, checkForCorrectAnswer: boolean = false) => {
        let hasIncorrect = false;
        let isSelectAllApplyExpected = false;

        if (correctAnswer.length > 1 && userResponse != correctAnswer) {
            isSelectAllApplyExpected = true;
            hasIncorrect = true;
        }

        let options: string[] = ['a', 'b', 'c', 'd'];
        for (var opt of options) {
            if (userResponse.toLowerCase().indexOf(opt) > -1) {
                let questionStatus = correctAnswer.toLowerCase().indexOf(opt) > -1 ? 'correct' : 'incorrect';
                if (questionStatus === 'incorrect') hasIncorrect = true;

                switch (opt) {
                    case 'a': {
                        setQuestionStatusA(questionStatus);
                        setIsAnswerAChecked(true);
                        break;
                    }
                    case 'b': {
                        setQuestionStatusB(questionStatus);
                        setIsAnswerBChecked(true);
                        break;
                    }
                    case 'c': {
                        setQuestionStatusC(questionStatus);
                        setIsAnswerCChecked(true);
                        break;
                    }
                    case 'd': {
                        setQuestionStatusD(questionStatus);
                        setIsAnswerDChecked(true);
                        break;
                    }
                }
            }


            if (checkForCorrectAnswer && correctAnswer.toLowerCase().indexOf(opt) > -1) {
                if (opt === 'a' && !isAnswerAChecked) {
                    hasIncorrect = true;
                    setIsAnswerAChecked(false);
                }

                if (opt === 'b' && !isAnswerBChecked) {
                    hasIncorrect = true;
                    setIsAnswerBChecked(false);
                }

                if (opt === 'c' && !isAnswerCChecked) {
                    hasIncorrect = true;
                    setIsAnswerCChecked(false);
                }

                if (opt === 'd' && !isAnswerDChecked) {
                    hasIncorrect = true;
                    setIsAnswerDChecked(false);
                }

            }
        }

        return { hasIncorrect, isSelectAllApplyExpected };
    }

    const setRadioChecked = (value: string) => {
        setIsAnswerAChecked(value === 'a');
        setIsAnswerBChecked(value === 'b');
        setIsAnswerCChecked(value === 'c');
        setIsAnswerDChecked(value === 'd');
    }

    const resetQuestionPopup = (questionNumberToResetTo = 0) => {
        setIsAnswerAChecked(false);
        setIsAnswerBChecked(false);
        setIsAnswerCChecked(false);
        setIsAnswerDChecked(false);

        setTextQuestionSummary("");
        setTextQuestionSummaryCSS("");
        setFinalAnswer("");

        setIsAnswerADisabled(false);
        setIsAnswerBDisabled(false);
        setIsAnswerCDisabled(false);
        setIsAnswerDDisabled(false);

        if (questionStatusA === 'incorrect') {
            setIsAnswerADisabled(true);
        }
        if (questionStatusB === 'incorrect') {
            setIsAnswerBDisabled(true);
        }
        if (questionStatusC === 'incorrect') {
            setIsAnswerCDisabled(true);
        }
        if (questionStatusD === 'incorrect') {
            setIsAnswerDDisabled(true);
        }

        setQuestionStatusA('');
        setQuestionStatusB('');
        setQuestionStatusC('');
        setQuestionStatusD('');

        setIsButtonRetryVisible(false);
        setIsButtonDoneVisible(false);
        setIsButtonSubmitVisible(true);

        // Disable incorrect answer options that were previously answered incorrectly
        let currentQuestion = recordsData.data[questionNumberToResetTo];
        currentQuestion.answerDetails.forEach(q => {
            if (!q.isCorrect) {
                if (q.answer.toLowerCase().includes("a") && !currentQuestion.correctAnswer.includes("a")) {
                    setIsAnswerADisabled(true);
                }

                if (q.answer.toLowerCase().includes("b") && !currentQuestion.correctAnswer.includes("b")) {
                    setIsAnswerBDisabled(true);
                }
                        
                if (q.answer.toLowerCase().includes("c") && !currentQuestion.correctAnswer.includes("c")) {
                    setIsAnswerCDisabled(true);
                }

                if (q.answer.toLowerCase().includes("d") && !currentQuestion.correctAnswer.includes("d")) {
                    setIsAnswerDDisabled(true);
                }
            }
        });
    }

    const renderQuestionPopup = () => {
        if (!recordsData.data || currentQuestionNumber >= recordsData.data.length) {
            return null;
        }

        return (
            <div className="countertop-university-quiz-popup">
                <span className="countertop-university-quiz-popup-title">{recordsData.data[currentQuestionNumber].question}</span>
                <p className={`countertop-university-quiz-popup-selectall ${recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? 'multiple' : 'single'}`}>Select all that apply</p>

                <div className="countertop-university-quiz-popup-questions-block">
                    <div className="countertop-university-quiz-popup-answer" >
                        <div className={`countertop-question ${isAnswerAChecked ? 'selected' : 'unselected'} ${questionStatusA}`} onClick={(e) => { if (!isAnswerADisabled) { recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? setIsAnswerAChecked(!isAnswerAChecked) : setRadioChecked('a'); } }}>
                            <input
                                className={(recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? "countertop-question-input checkboxAnswer" : "countertop-question-input radioAnswer")}
                                name={recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? "checkboxAnswer" : "radioAnswer"}
                                type={recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? "checkbox" : "radio"}
                                disabled={isAnswerADisabled}
                                checked={isAnswerAChecked}
                                onChange={(e) => {
                                    recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? setIsAnswerAChecked(e.target.checked) : setRadioChecked('a');
                                }}
                            />
                            <span className="countertop-question-answer" style={{ marginTop: (recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? '' : '4px') }}>{recordsData.data[currentQuestionNumber].answerA}</span>
                        </div>

                        <div className={`countertop-question ${isAnswerBChecked ? 'selected' : 'unselected'} ${questionStatusB}`} onClick={(e) => { if (!isAnswerBDisabled) { recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? setIsAnswerBChecked(!isAnswerBChecked) : setRadioChecked('b'); } }}>
                            <input
                                className={recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? "countertop-question-input checkboxAnswer" : "countertop-question-input radioAnswer"}
                                name={recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? "checkboxAnswer" : "radioAnswer"}
                                type={recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? "checkbox" : "radio"}
                                disabled={isAnswerBDisabled}
                                checked={isAnswerBChecked}
                                onChange={(e) => {
                                    recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? setIsAnswerBChecked(e.target.checked) : setRadioChecked('b');
                                }}
                            />
                            <span className="countertop-question-answer" style={{ marginTop: (recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? '' : '4px') }}>{recordsData.data[currentQuestionNumber].answerB}</span>
                        </div>

                        <div className={`countertop-question ${isAnswerCChecked ? 'selected' : 'unselected'} ${questionStatusC}`} onClick={(e) => { if (!isAnswerCDisabled) { recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? setIsAnswerCChecked(!isAnswerCChecked) : setRadioChecked('c'); } }}>
                            <input
                                className={recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? "countertop-question-input checkboxAnswer" : "countertop-question-input radioAnswer"}
                                name={recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? "checkboxAnswer" : "radioAnswer"}
                                type={recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? "checkbox" : "radio"}
                                disabled={isAnswerCDisabled}
                                checked={isAnswerCChecked}
                                onChange={(e) => {
                                    recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? setIsAnswerCChecked(e.target.checked) : setRadioChecked('c');
                                }}
                            />
                            <span className="countertop-question-answer" style={{ marginTop: (recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? '' : '4px') }}>{recordsData.data[currentQuestionNumber].answerC}</span>
                        </div>

                        <div className={`countertop-question ${isAnswerDChecked ? 'selected' : 'unselected'} ${questionStatusD}`} onClick={(e) => { if (!isAnswerDDisabled) { recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? setIsAnswerDChecked(!isAnswerDChecked) : setRadioChecked('d'); } }}>
                            <input
                                className={recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? "countertop-question-input checkboxAnswer" : "countertop-question-input radioAnswer"}
                                name={recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? "checkboxAnswer" : "radioAnswer"}
                                type={recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? "checkbox" : "radio"}
                                disabled={isAnswerDDisabled}
                                checked={isAnswerDChecked}
                                onChange={(e) => {
                                    recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? setIsAnswerDChecked(e.target.checked) : setRadioChecked('d');
                                }}
                            />
                            <span className="countertop-question-answer" style={{ marginTop: (recordsData.data[currentQuestionNumber].correctAnswer.length > 1 ? '' : '4px') }}>{recordsData.data[currentQuestionNumber].answerD}</span>
                        </div>
                    </div>
                </div>

                <div className="countertop-university-quiz-popup-buttons">
                    <p className={`${textQuestionSummary.length <= 0 ? 'countertop-question-summary' : textQuestionSummaryCSS}`}>{textQuestionSummary}</p>
                    <Button
                        className="countertop-university-submit-btn"
                        icon=""
                        visible={isButtonSubmitVisible}
                        text="Submit"
                        type='default'
                        width={'120px'}
                        onClick={() => {
                            let isCorrect = checkAnswer();
                            setIsCorrect(isCorrect);

                            if (isCorrect)
                                setShouldSilentlySave(false);
                            else
                                setShouldSilentlySave(true);

                            saveAnswerQuiz.mutate();
                        }}>
                    </Button>

                    <Button
                        className="countertop-university-retry-btn"
                        icon=""
                        visible={isButtonRetryVisible}
                        text="Retry"
                        type='default'
                        width={'120px'}
                        onClick={() => resetQuestionPopup(currentQuestionNumber)}>
                    </Button>

                    <Button
                    className="countertop-university-done-btn"
                        icon=""
                        disabled={recordsData.data.length === 0}
                        visible={isButtonDoneVisible}
                        text={"Done"}
                        type='default'
                        width={'120px'}
                        onClick={async () => {
                            toggleQuestionPopup();
                            setShouldSilentlySave(false);
                            await recordsData.refetch();
                            if (props.onQuizComplete) {
                                props.onQuizComplete();
                            }
                        }}>
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <>
            <Popup
                title={`Question ${currentQuestionNumber + 1} of  ${recordsData.data.length}`}
                visible={isQuestionPopupVisible}
                hideOnOutsideClick={false}
                width={800}
                height={525}
                onHiding={toggleQuestionPopup}
                showCloseButton={true}
                contentRender={renderQuestionPopup}
            >
            </Popup>

            <div className="countertop-university-cards countertop-university-quiz-submission">
                <div className="countertop-university-quiz-submission-block">
                    <span className="countertop-university-quiz-title-item-quiz-submission">Quiz Submission</span>
                    <span className="countertop-university-quiz-title-subitem">{props.item.title}</span>
                    <span className="countertop-university-quiz-title-subitem">{props.item.descriptionTitle}</span>
                </div>

                <div>
                    <Button
                        className="countertop-university-take-quiz-btn"
                        icon=""
                        disabled={!props.item.hasQuiz || props.item.quizDone || !props.item.videoDone}
                        text="Take Quiz"
                        type='default'
                        width={'120px'}
                        onClick={() => {
                            resetQuestionPopup();
                            setIsQuestionPopupVisible(!isQuestionPopupVisible);

                            let index = recordsData.data.findIndex(f => !f.answerDetails.some(x => x.isCorrect));
                            setCurrentQuestionNumber(index);
                        }} >
                    </Button>
                </div>

                <div className="countertop-question-right-block">
                    <span className="countertop-question-video-completion">
                        <img className="countertop-university-quiz-img" src={props.item.videoDone ? CompletedItemIcon : IncompleteVideoIcon} alt="Icon" />&nbsp;&nbsp;Video Completion
                    </span>

                    {recordsData.data.map((question, index) => {
                        const isExpanded = expandedIndex === index;
                        const hasAttempts = question.answerDetails?.length > 0;

                        return (
                            <span key={'q' + index} className="countertop-question-block" style={{ height: question.completedOn ? '50px' : '30px' }}>
                                <CountertopProfileQuizBlock
                                    hasAttempts={hasAttempts}
                                    index={index}
                                    question={question}
                                    isExpanded={isExpanded}
                                    isTakeQuizDisabled={!props.item.hasQuiz || props.item.quizDone || !props.item.videoDone}
                                    setIsQuestionPopupVisible={setIsQuestionPopupVisible}
                                    setCurrentQuestionNumber={setCurrentQuestionNumber}
                                    setExpandedIndex={setExpandedIndex}
                                    resetQuestionPopup={resetQuestionPopup}
                                ></CountertopProfileQuizBlock>

                                {hasAttempts && (
                                    <CountertopUniversityQuestionAttempts
                                        controlResponses={controlResponses}
                                        index={index}
                                        isExpanded={isExpanded}
                                        question={question}
                                        resetQuestionPopup={resetQuestionPopup}
                                        setIsButtonDoneVisible={setIsButtonDoneVisible}
                                        setIsButtonRetryVisible={setIsButtonRetryVisible}
                                        setIsButtonSubmitVisible={setIsButtonSubmitVisible}
                                        setIsQuestionPopupVisible={setIsQuestionPopupVisible}
                                        setCurrentQuestionNumber={setCurrentQuestionNumber}
                                        setIsAnswerADisabled={setIsAnswerADisabled}
                                        setIsAnswerBDisabled={setIsAnswerBDisabled}
                                        setIsAnswerCDisabled={setIsAnswerCDisabled}
                                        setIsAnswerDDisabled={setIsAnswerDDisabled}
                                        setTextQuestionSummary={setTextQuestionSummary}
                                        setTextQuestionSummaryCSS={setTextQuestionSummaryCSS}
                                    ></CountertopUniversityQuestionAttempts>
                                )}
                            </span>
                        );
                    })}

                </div>
            </div>
        </>
    )
}
